import React, { useState } from 'react';
import { Mail, Send, CheckCircle2, MessageSquare, HeartHandshake } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { motion } from 'motion/react';
import { apiFetch } from '@/lib/api';
import { DiscordCTA } from '@/components/DiscordCTA';
import InternalLinksSection from '@/components/InternalLinksSection';

const Contact: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    website: '' // Honeypot
  });
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Honeypot check
    if (formData.website) {
      setStatus('success');
      return;
    }

    setStatus('loading');
    setErrorMessage('');

    try {
      const response = await apiFetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setStatus('success');
        setFormData({ name: '', email: '', subject: '', message: '', website: '' });
      } else {
        const data = await response.json();
        setErrorMessage(data.error || 'Failed to send message.');
        setStatus('error');
      }
    } catch (error) {
      setErrorMessage('Failed to connect to the server.');
      setStatus('error');
    }
  };

  return (
    <div className="min-h-screen pt-24 pb-24 bg-white relative overflow-hidden">
      {/* Background accents */}
      <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-cyan-100/20 rounded-full blur-[140px] -z-10 animate-pulse duration-[10s]" />
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-cyan-50/30 rounded-full blur-[120px] -z-10" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full bg-[radial-gradient(rgba(6,182,212,0.05)_1px,transparent_1px)] [background-size:40px_40px] [mask-image:radial-gradient(ellipse_60%_60%_at_50%_50%,#000_70%,transparent_100%)] opacity-20 -z-10" />

      <div className="max-w-6xl mx-auto px-6 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          <motion.div 
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="space-y-8"
          >
            <div className="space-y-4">
              <h1 className="text-4xl md:text-5xl font-bold font-display leading-tight tracking-tighter uppercase">
                <span className="bg-clip-text text-transparent bg-gradient-to-r from-cyan-600 via-cyan-400 to-cyan-600">Contact</span> <br />
                <span className="text-zinc-400">Us</span>
              </h1>
              <p className="text-sm text-zinc-500 font-medium leading-relaxed max-w-sm font-sans">
                Whether you're a content creator needing help or a partner with a proposal, our team is ready to assist.
              </p>
            </div>

            <div className="grid grid-cols-1 gap-4 pt-4">
              {[
                { 
                  label: 'Technical Support', 
                  email: 'support@funcirclegames.com', 
                  icon: MessageSquare, 
                  color: 'text-cyan-500',
                  bg: 'bg-cyan-50',
                  subject: 'Support Request'
                },
                { 
                  label: 'General Inquiry', 
                  email: 'contact@funcirclegames.com', 
                  icon: HeartHandshake, 
                  color: 'text-cyan-600',
                  bg: 'bg-cyan-100/50',
                  subject: 'General Question'
                },
              ].map((item) => (
                <motion.a 
                  key={item.label} 
                  href={`mailto:${item.email}?subject=${encodeURIComponent(item.subject)}`}
                  whileHover={{ scale: 1.01, x: 5 }}
                  whileTap={{ scale: 0.99 }}
                  className="p-6 bg-white/50 backdrop-blur-sm rounded-3xl border border-cyan-100 flex items-center justify-between group hover:border-cyan-500/50 hover:bg-white transition-all duration-300 shadow-lg shadow-cyan-500/5"
                >
                  <div className="space-y-0.5">
                    <span className="text-[9px] font-black uppercase tracking-[0.2em] text-cyan-400 group-hover:text-cyan-600 transition-colors">{item.label}</span>
                    <p className="text-zinc-950 font-bold tracking-tight text-lg font-display uppercase">{item.email}</p>
                  </div>
                  <div className={`w-10 h-10 ${item.bg} rounded-xl flex items-center justify-center border border-cyan-100 group-hover:shadow-lg group-hover:shadow-cyan-500/10 transition-all duration-300 text-cyan-500`}>
                    <item.icon className="w-5 h-5" />
                  </div>
                </motion.a>
              ))}
            </div>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="bg-zinc-50/50 backdrop-blur-sm p-8 md:p-10 rounded-[2.5rem] border border-cyan-100 shadow-xl shadow-cyan-500/5 relative overflow-hidden"
          >
            {/* Form Background Pattern */}
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_100%_100%,rgba(6,182,212,0.02)_0%,transparent_50%)] -z-10" />

            {status === 'success' ? (
              <div className="text-center py-12 animate-in fade-in zoom-in duration-700">
                <div className="w-20 h-20 bg-cyan-600 rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-xl shadow-cyan-600/20">
                  <CheckCircle2 className="text-white w-10 h-10" />
                </div>
                <h2 className="text-2xl font-bold font-display text-zinc-950 uppercase tracking-tight mb-2 text-cyan-600">Message Sent</h2>
                <p className="text-xs text-zinc-500 font-medium mb-8 px-6 font-sans">Thank you for reaching out. We will get back to you shortly.</p>
                <Button 
                  onClick={() => setStatus('idle')}
                  className="px-10 h-14 bg-cyan-600 text-white rounded-xl font-black uppercase tracking-widest shadow-xl shadow-cyan-600/20 hover:bg-cyan-700 transition-all font-sans"
                >
                  Send Another
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6 font-sans">
                {/* Honeypot field */}
                <div className="hidden" aria-hidden="true">
                  <input 
                    type="text" 
                    name="website" 
                    value={formData.website} 
                    onChange={(e) => setFormData({...formData, website: e.target.value})} 
                    tabIndex={-1} 
                    autoComplete="off" 
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-[9px] font-black uppercase tracking-[0.2em] text-cyan-500/60 ml-1 font-sans">Full Name</label>
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      className="w-full px-5 py-4 bg-white border border-cyan-100 rounded-xl text-zinc-950 font-medium placeholder:text-zinc-300 focus:ring-4 focus:ring-cyan-500/5 focus:border-cyan-500 outline-none transition-all font-sans text-sm"
                      placeholder="Jane Doe"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-[9px] font-black uppercase tracking-[0.2em] text-cyan-500/60 ml-1 font-sans">Email Address</label>
                    <input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      className="w-full px-5 py-4 bg-white border border-cyan-100 rounded-xl text-zinc-950 font-medium placeholder:text-zinc-300 focus:ring-4 focus:ring-cyan-500/5 focus:border-cyan-500 outline-none transition-all font-sans text-sm"
                      placeholder="jane@example.com"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-[9px] font-black uppercase tracking-[0.2em] text-cyan-500/60 ml-1 font-sans">Subject of Inquiry</label>
                  <input
                    type="text"
                    required
                    value={formData.subject}
                    onChange={(e) => setFormData({...formData, subject: e.target.value})}
                    className="w-full px-5 py-4 bg-white border border-cyan-100 rounded-xl text-zinc-950 font-medium placeholder:text-zinc-300 focus:ring-4 focus:ring-cyan-500/5 focus:border-cyan-500 outline-none transition-all font-sans text-sm"
                    placeholder="General Inquiry"
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-[9px] font-black uppercase tracking-[0.2em] text-cyan-500/60 ml-1 font-sans">Detailed Message</label>
                  <textarea
                    required
                    rows={4}
                    value={formData.message}
                    onChange={(e) => setFormData({...formData, message: e.target.value})}
                    className="w-full px-5 py-4 bg-white border border-cyan-100 rounded-xl text-zinc-950 font-medium placeholder:text-zinc-300 focus:ring-4 focus:ring-cyan-500/5 focus:border-cyan-500 outline-none transition-all resize-none font-sans text-sm"
                    placeholder="Tell us everything..."
                  />
                </div>

                {status === 'error' && (
                  <motion.div 
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="p-4 bg-red-50 border border-red-100 rounded-xl text-red-500 text-[9px] font-black text-center font-sans tracking-widest uppercase"
                  >
                    Error: {errorMessage}
                  </motion.div>
                )}

                <Button
                  type="submit"
                  disabled={status === 'loading'}
                  className="w-full h-16 bg-cyan-600 text-white font-black uppercase tracking-[0.2em] text-[10px] rounded-xl shadow-xl shadow-cyan-600/10 active:scale-[0.98] transition-all flex items-center justify-center gap-3 disabled:opacity-50 hover:bg-cyan-700 font-sans mt-4"
                >
                  {status === 'loading' ? (
                    'Transmitting...'
                  ) : (
                    <>
                      <Send className="w-3.5 h-3.5 mr-1" />
                      Dispatch Message
                    </>
                  )}
                </Button>
              </form>
            )}
          </motion.div>
        </div>

        {/* Faster Support & Community Hub */}
        <div className="mt-24 pt-16 border-t border-zinc-100 grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          <div className="space-y-6">
            <h3 className="text-2xl font-bold font-display uppercase tracking-tight text-zinc-950">Faster Support via Discord</h3>
            <p className="text-zinc-500 text-sm font-medium font-sans leading-relaxed">
              Email inquiries can take up to 24 hours. For instant support, live chat, or community collaboration, join our official Discord server. 
            </p>
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 rounded-xl border border-zinc-100 bg-zinc-50/50">
                <h4 className="text-xs font-bold text-zinc-950 uppercase mb-1">Bug Reports</h4>
                <p className="text-[10px] text-zinc-500 font-medium">Submit logs directly to developers.</p>
              </div>
              <div className="p-4 rounded-xl border border-zinc-100 bg-zinc-50/50">
                <h4 className="text-xs font-bold text-zinc-950 uppercase mb-1">Feature Requests</h4>
                <p className="text-[10px] text-zinc-500 font-medium">Vote on the next simulation modes.</p>
              </div>
              <div className="p-4 rounded-xl border border-zinc-100 bg-zinc-50/50">
                <h4 className="text-xs font-bold text-zinc-950 uppercase mb-1">Beta Testing</h4>
                <p className="text-[10px] text-zinc-500 font-medium">Get early access keys to active updates.</p>
              </div>
              <div className="p-4 rounded-xl border border-zinc-100 bg-zinc-50/50">
                <h4 className="text-xs font-bold text-zinc-950 uppercase mb-1">Discussions</h4>
                <p className="text-[10px] text-zinc-500 font-medium">Share tips on ASMR video creation.</p>
              </div>
            </div>
          </div>
          
          <div className="h-full flex flex-col justify-center">
            <DiscordCTA variant="card" className="shadow-xl shadow-cyan-600/5 hover:-translate-y-1 transition-all duration-300 border border-cyan-100 bg-white" />
          </div>
        </div>

        <div className="mt-16">
          <InternalLinksSection />
        </div>
      </div>
    </div>
  );
};

export default Contact;

